import cn from 'classnames'
import React, { ReactElement } from 'react'
import promotionFreeSeatsStar from 'src/assets/svgs/PromotionFreeSeatsStar.svg'
import promotionSavingsStar from 'src/assets/svgs/PromotionSavingsStar.svg'
import { ExtendCurrency } from 'src/enums'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import {
  PricingPromotion,
  PricingPromotionInterval,
} from 'src/queries/organizations/pricingPlans/pricingPlans.models'

import { PricingPresentationAdapter } from './pricingPresentation'
import style from './PromotionSavingsHero.style'

export type PromotionBillingCycle = 'MONTHLY' | 'YEARLY'

interface PromotionSavingsHeroProps {
  className?: string
  promotion: PricingPromotion
  billingCycle: PromotionBillingCycle
  currencyFormatter: CurrencyFormatter
  presentation?: PricingPresentationAdapter
}

const getInterval = (
  promotion: PricingPromotion,
  billingCycle: PromotionBillingCycle,
): PricingPromotionInterval =>
  billingCycle === 'MONTHLY'
    ? promotion.billingIntervals.monthly
    : promotion.billingIntervals.yearly

const getCurrency = (currency: string): ExtendCurrency =>
  currency === ExtendCurrency.Cad ? ExtendCurrency.Cad : ExtendCurrency.Usd

export default function PromotionSavingsHero(
  props: PromotionSavingsHeroProps,
): ReactElement {
  const {
    className = '',
    promotion,
    billingCycle,
    currencyFormatter,
    presentation,
  } = props
  const interval = getInterval(promotion, billingCycle)
  const currency = getCurrency(promotion.currency)
  const translateApiText = presentation?.translateApiText ?? ((source) => source)
  const formatCents = (amountCents: number): string =>
    presentation
      ? presentation.formatCurrencyAmount(amountCents / 100)
      : currencyFormatter({ amount: amountCents / 100, currency })

  return (
    <section
      className={cn('PromotionSavingsHero', className)}
      css={style}
      data-testid="promotion-savings-hero"
      aria-label={translateApiText(promotion.displayName)}
    >
      <div className="PromotionSavingsHero-copy">
        <div className="PromotionSavingsHero-brand">
          <img
            src={promotion.logoUrl}
            alt=""
            className="PromotionSavingsHero-logo"
          />
          <p className="PromotionSavingsHero-eyebrow">
            {translateApiText(interval.copy.eyebrow)}
          </p>
        </div>
        <h2 className="PromotionSavingsHero-headline">
          {translateApiText(interval.copy.headline)}
        </h2>
        <p className="PromotionSavingsHero-description">
          {translateApiText(interval.copy.description)}
        </p>
      </div>
      <div
        className="PromotionSavingsHero-pricing"
        data-testid="promotion-savings-hero-savings-panel"
        aria-live="polite"
        aria-atomic="true"
      >
        <p className="PromotionSavingsHero-firstYearPriceLabel">
          <img
            alt=""
            data-testid="promotion-savings-eyebrow-star"
            src={promotionSavingsStar}
          />
          {translateApiText(interval.firstYearPriceLabel)}
        </p>
        <div className="PromotionSavingsHero-firstYearPriceSummary">
          <p className="PromotionSavingsHero-firstYearPriceAmount">
            {formatCents(interval.discountedFirstYearCents)}
          </p>
          <p className="PromotionSavingsHero-savingsDetail">
            {translateApiText(interval.savingsDetail)}
          </p>
        </div>
        <div className="PromotionSavingsHero-divider" />
        <p
          className="PromotionSavingsHero-paymentSummary"
          data-testid="promotion-savings-hero-payment-summary"
        >
          {`${translateApiText(interval.priceSummaryPrefix)} `}
          <span className="PromotionSavingsHero-discountedPrice">
            {formatCents(interval.discountedFirstYearCents)}
          </span>
          {` ${translateApiText(interval.priceSummaryComparison)} `}
          <s className="PromotionSavingsHero-standardPrice">
            {formatCents(interval.standardFirstYearCents)}
          </s>
        </p>
        <p
          className="PromotionSavingsHero-freeSeats"
          data-testid="promotion-savings-hero-free-seats"
        >
          <img
            alt=""
            data-testid="promotion-free-seats-star"
            src={promotionFreeSeatsStar}
          />
          {translateApiText(interval.freeSeatsLabel)}
        </p>
      </div>
    </section>
  )
}
